import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { getSafeColorListByTheme } from '@/common/utils/chartUtils';
import { CUSTOM_KEY } from '@/dashboard/features/widgets/common/constants/define';
import { CHART_COLORS_SPARROW } from '@/common/utils';
import { getCurrentTheme } from '@/common/utils/commonUtils';
import type { TargetInfo } from '@/worker/commands/dashboard/metrics';
import router from '@/common/router';

type TargetId = string;
type TargetIndex = number;
export type SerializedDashboardTargetIndexMap = Array<[TargetId, TargetIndex]>;

const colorListCache = new Map<string, string[]>();

const getThemeColorList = (curTheme: string) => {
  const productTheme = getCurrentTheme();
  const cacheKey = `${productTheme}:${curTheme}`;
  const cached = colorListCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const list = curTheme === CUSTOM_KEY ? CHART_COLORS_SPARROW : getSafeColorListByTheme(curTheme);
  colorListCache.set(cacheKey, list);
  return list;
};

export const useDashboardTargetsColorSyncStore = defineStore('dashboardTargetsColorSync', () => {
  const targetIndexMap = ref<Map<TargetId, TargetIndex>>(new Map());

  const currentDashboardId = computed(
    () => router.currentRoute.value.params.id || router.currentRoute.value.query.id,
  );

  const setTargetIndexMap = (targets: TargetInfo[]) => {
    targets.forEach(({ targetId }) => {
      if (!targetIndexMap.value.has(targetId)) {
        targetIndexMap.value.set(targetId, targetIndexMap.value.size);
      }
    });
  };

  const getDashboardTargetColor = ({
    colorTheme,
    targetId,
    colorIndex,
  }: {
    colorTheme: string;
    targetId: TargetId;
    colorIndex: number;
  }) => {
    const colorList = getThemeColorList(colorTheme);
    const storedIndex = targetIndexMap.value.get(targetId);

    if (storedIndex !== undefined) {
      return colorList[storedIndex % colorList.length];
    }
    return colorList[colorIndex % colorList.length];
  };

  const getAllTargetColors = (colorTheme: string): Record<string, string> => {
    const colorList = getThemeColorList(colorTheme);
    const colors: Record<string, string> = {};
    targetIndexMap.value.forEach((index, targetId) => {
      colors[targetId] = colorList[index % colorList.length];
    });
    return colors;
  };

  const clearTargetIndexMap = () => {
    targetIndexMap.value.clear();
  };

  const replaceTargetIndexMap = (entries: SerializedDashboardTargetIndexMap = []) => {
    targetIndexMap.value = new Map(entries);
  };

  watch(currentDashboardId, clearTargetIndexMap);

  return {
    targetIndexMap,
    setTargetIndexMap,
    replaceTargetIndexMap,
    getDashboardTargetColor,
    getAllTargetColors,
  };
});
